import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import FetchModule from '../../Share/Network/FetchModule';

import AttendanceChart from './AttendanceChart';
import ClassAttendanceList from './ClassAttendanceList';
import AttendanceRankingList from './AttendanceRankingList';

import LoadingAnimation from '../../../Animation/NewLoading.json';

import { ReactComponent as CheckSvg } from '../../../image/Check.svg';

export default function AttendanceBox(props) {
  const {
    today,
    startDate,
    endDate,
    calcDateText,

    attendanceTotal,
    attendanceRanking,
    totalMonthAttendanceCount,

    selectedPartnerGym,
    isOpenPartnerGymSales,
    isAttendanceLoading,
    setIsAttendanceLoading,

    frequencyData,
    isFrequencyLoading,
  } = props;

  const history = useHistory();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [viewMode, setViewMode] = useState('monthly');
  const [isCountOncePerDay, setIsCountOncePerDay] = useState(true);

  const [yearlyAttendanceData, setYearlyAttendanceData] = useState([]);

  // 출석 데이터 로딩 완료 처리 //
  useEffect(() => {
    if (!startDate || !endDate) return;

    let isLastest = true;

    if (isLastest) {
      setTimeout(() => {
        setIsAttendanceLoading(false);
      }, 1000);
    }

    return () => {
      isLastest = false;
    }
  }, [isAttendanceLoading, attendanceTotal, startDate, endDate]);

  // 월간 차트용 연간 출석 데이터 호출 //
  useEffect(() => {
    let isLastest = true;

    const yearStart = moment(today.format('YYYY') + '-01-01T00:00:00');
    const yearEnd = moment(today.format('YYYY') + '-12-31T23:59:59');

    if (selectedPartnerGym === undefined || selectedPartnerGym.isMyStore) {
      let requestData = {
        startDate: yearStart.format('YYYY-MM-DDTHH:mm:ss'),
        endDate: yearEnd.format('YYYY-MM-DDTHH:mm:ss')
      };

      const fetchModule = new FetchModule();
      (
        async () => {
          const responseData = await fetchModule.postDataVersion2('sales/monthly/attendance', 'POST', requestData);

          if (responseData.status === 200) {
            if (isLastest) {
              setYearlyAttendanceData(responseData.data);
            }
          }
          else if (responseData.status === 401) history.push('/');
          else {
            if (isLastest) {
              setYearlyAttendanceData([]);
            }
          }
        }
      )();
    }
    else {
      if (selectedPartnerGym.isOpenPartnerGymSales) {
        let requestData = {
          storeId: selectedPartnerGym.storeId,
          startDate: yearStart.format('YYYY-MM-DDTHH:mm:ss'),
          endDate: yearEnd.format('YYYY-MM-DDTHH:mm:ss')
        };

        const fetchModule = new FetchModule();
        (
          async () => {
            const responseData = await fetchModule.postDataVersion2('sales/monthly/attendance/partner_gym', 'POST', requestData);

            if (responseData.status === 200) {
              if (isLastest) {
                setYearlyAttendanceData(responseData.data);
              }
            }
            else if (responseData.status === 401) history.push('/');
            else {
              if (isLastest) {
                setYearlyAttendanceData([]);
              }
            }
          }
        )();
      }
      else {
        setYearlyAttendanceData([]);
      }
    }

    return () => { isLastest = false; }
  }, [today, selectedPartnerGym]);

  // 차트 데이터 생성 //
  const chartData = useMemo(() => {
    // 월간: 1월~12월 월별 출석 횟수 //
    if (viewMode === 'monthly') {
      const monthLabels = [
        t('statistics.chart.month1'), t('statistics.chart.month2'), t('statistics.chart.month3'),
        t('statistics.chart.month4'), t('statistics.chart.month5'), t('statistics.chart.month6'),
        t('statistics.chart.month7'), t('statistics.chart.month8'), t('statistics.chart.month9'),
        t('statistics.chart.month10'), t('statistics.chart.month11'), t('statistics.chart.month12')
      ];

      const result = [];

      for (let month = 0; month < 12; month++) {
        let count = 0;
        const monthStr = String(month + 1).padStart(2, '0');

        for (let i = 0; i < yearlyAttendanceData.length; i++) {
          const member = yearlyAttendanceData[i];

          if (isCountOncePerDay) {
            const uniqueDates = new Set();

            for (let j = 0; j < member.reserveAttendances.length; j++) {
              const classDate = member.reserveAttendances[j].classDate;

              if (classDate.substring(5, 7) === monthStr) {
                uniqueDates.add(classDate.substring(0, 10));
              }
            }

            count += uniqueDates.size;
          }
          else {
            for (let j = 0; j < member.reserveAttendances.length; j++) {
              if (member.reserveAttendances[j].classDate.substring(5, 7) === monthStr) {
                count += 1;
              }
            }
          }
        }

        result.push({
          day: month + 1,
          label: monthLabels[month],
          count: count
        });
      }

      return result;
    }
    // 일간: 해당 월 1일~말일 일별 출석 횟수 //
    else {
      const rangeStart = startDate;
      const rangeEnd = endDate;

      if (!rangeStart || !rangeEnd) return [];

      const result = [];
      const current = rangeStart.clone();

      while (current.isSameOrBefore(rangeEnd, 'day')) {
        const dayStr = current.format('YYYY-MM-DD');
        let count = 0;

        if (isCountOncePerDay) {
          for (let i = 0; i < attendanceTotal.length; i++) {
            for (let j = 0; j < attendanceTotal[i].reserveAttendances.length; j++) {
              if (attendanceTotal[i].reserveAttendances[j].classDate.substring(0, 10) === dayStr) {
                count += 1;
                break;
              }
            }
          }
        }
        else {
          for (let i = 0; i < attendanceTotal.length; i++) {
            for (let j = 0; j < attendanceTotal[i].reserveAttendances.length; j++) {
              if (attendanceTotal[i].reserveAttendances[j].classDate.substring(0, 10) === dayStr) {
                count += 1;
              }
            }
          }
        }

        result.push({
          day: current.date(),
          label: String(current.date()),
          count: count
        });

        current.add(1, 'day');
      }

      return result;
    }
  }, [viewMode, attendanceTotal, yearlyAttendanceData, startDate, endDate, isCountOncePerDay, t]);

  // 출석률 계산 //
  // 일간: Σ(당일 출석 / 당일 유효 회원) / 일수 × 100
  // 월간: Σ(당월 출석 / 당월 유효 회원) / 월수 × 100
  function calcAttendanceRate() {
    if (viewMode === 'monthly') {
      const currentMonth = today.month();
      let totalRate = 0;
      let monthCount = 0;

      for (let month = 0; month <= currentMonth; month++) {
        const monthStr = String(month + 1).padStart(2, '0');
        const monthStart = moment(today.format('YYYY') + '-' + monthStr + '-01');

        let validCount = 0;
        for (let i = 0; i < yearlyAttendanceData.length; i++) {
          if (moment(yearlyAttendanceData[i].endDate).isSameOrAfter(monthStart, 'day')
            && moment(yearlyAttendanceData[i].startDate).isSameOrBefore(monthStart, 'day')) {
            validCount++;
          }
        }

        if (validCount === 0) continue;

        let attendCount = 0;

        if (isCountOncePerDay) {
          for (let i = 0; i < yearlyAttendanceData.length; i++) {
            if (!moment(yearlyAttendanceData[i].endDate).isSameOrAfter(monthStart, 'day')
              || !moment(yearlyAttendanceData[i].startDate).isSameOrBefore(monthStart, 'day')) continue;
            let attended = false;
            for (let j = 0; j < yearlyAttendanceData[i].reserveAttendances.length; j++) {
              if (yearlyAttendanceData[i].reserveAttendances[j].classDate.substring(5, 7) === monthStr) {
                attended = true;
                break;
              }
            }
            if (attended) attendCount++;
          }
        }
        else {
          for (let i = 0; i < yearlyAttendanceData.length; i++) {
            if (!moment(yearlyAttendanceData[i].endDate).isSameOrAfter(monthStart, 'day')
              || !moment(yearlyAttendanceData[i].startDate).isSameOrBefore(monthStart, 'day')) continue;
            for (let j = 0; j < yearlyAttendanceData[i].reserveAttendances.length; j++) {
              if (yearlyAttendanceData[i].reserveAttendances[j].classDate.substring(5, 7) === monthStr) {
                attendCount++;
              }
            }
          }
        }

        totalRate += attendCount / validCount;
        monthCount++;
      }

      if (monthCount === 0) return 0;
      return Math.round((totalRate / monthCount) * 100);
    }
    else {
      if (!startDate || !endDate) return 0;

      const now = moment();
      let totalRate = 0;
      let dayCount = 0;
      const current = startDate.clone();

      while (current.isSameOrBefore(endDate, 'day') && current.isSameOrBefore(now, 'day')) {
        const dayStr = current.format('YYYY-MM-DD');

        let validCount = 0;
        for (let i = 0; i < attendanceTotal.length; i++) {
          if (moment(attendanceTotal[i].endDate).isSameOrAfter(current, 'day')
            && moment(attendanceTotal[i].startDate).isSameOrBefore(current, 'day')) {
            validCount++;
          }
        }

        if (validCount === 0) {
          current.add(1, 'day');
          continue;
        }

        let attendCount = 0;

        if (isCountOncePerDay) {
          for (let i = 0; i < attendanceTotal.length; i++) {
            if (!moment(attendanceTotal[i].endDate).isSameOrAfter(current, 'day')
              || !moment(attendanceTotal[i].startDate).isSameOrBefore(current, 'day')) continue;
            for (let j = 0; j < attendanceTotal[i].reserveAttendances.length; j++) {
              if (attendanceTotal[i].reserveAttendances[j].classDate.substring(0, 10) === dayStr) {
                attendCount++;
                break;
              }
            }
          }
        }
        else {
          for (let i = 0; i < attendanceTotal.length; i++) {
            if (!moment(attendanceTotal[i].endDate).isSameOrAfter(current, 'day')
              || !moment(attendanceTotal[i].startDate).isSameOrBefore(current, 'day')) continue;
            for (let j = 0; j < attendanceTotal[i].reserveAttendances.length; j++) {
              if (attendanceTotal[i].reserveAttendances[j].classDate.substring(0, 10) === dayStr) {
                attendCount++;
              }
            }
          }
        }

        totalRate += attendCount / validCount;
        dayCount++;
        current.add(1, 'day');
      }

      if (dayCount === 0) return 0;
      return Math.round((totalRate / dayCount) * 100);
    }
  }

  // 출석 인원 수 //
  function calcAttendedCount() {
    const data = viewMode === 'monthly' ? yearlyAttendanceData : attendanceTotal;
    const rangeStart = viewMode === 'monthly'
      ? moment(today.format('YYYY') + '-01-01')
      : startDate;
    const rangeEnd = viewMode === 'monthly'
      ? moment(today.format('YYYY') + '-12-31')
      : endDate;

    if (!rangeStart || !rangeEnd || data.length === 0) return 0;

    if (isCountOncePerDay) {
      // 중복 불인정: 기간 내 1회 이상 출석한 회원 수
      let count = 0;
      for (let i = 0; i < data.length; i++) {
        let attended = false;
        for (let j = 0; j < data[i].reserveAttendances.length; j++) {
          const classDate = moment(data[i].reserveAttendances[j].classDate);
          if (classDate.isSameOrAfter(rangeStart, 'day') && classDate.isSameOrBefore(rangeEnd, 'day')) {
            attended = true;
            break;
          }
        }
        if (attended) count++;
      }
      return count;
    }
    else {
      // 중복 인정: 기간 내 총 출석 건수
      let count = 0;
      for (let i = 0; i < data.length; i++) {
        for (let j = 0; j < data[i].reserveAttendances.length; j++) {
          const classDate = moment(data[i].reserveAttendances[j].classDate);
          if (classDate.isSameOrAfter(rangeStart, 'day') && classDate.isSameOrBefore(rangeEnd, 'day')) {
            count++;
          }
        }
      }
      return count;
    }
  }

  // 일별 1회만 집계한 출석 랭킹 계산 //
  function calculateOncePerDayRanking(ranking) {
    const result = ranking.map(member => {
      const uniqueDates = new Set();

      for (const attendance of member.reserveAttendances) {
        const dateOnly = attendance.classDate.substring(0, 10);
        uniqueDates.add(dateOnly);
      }

      return { ...member, attendanceCount: uniqueDates.size };
    });

    result.sort((a, b) => b.attendanceCount - a.attendanceCount);

    return result;
  }

  // 랭킹 데이터 (월간 고정) //
  const processedAttendanceRanking = useMemo(() => {
    return isCountOncePerDay
      ? calculateOncePerDayRanking(attendanceRanking)
      : attendanceRanking;
  }, [attendanceRanking, isCountOncePerDay]);

  // 로딩 상태 //
  const isLoading = isAttendanceLoading || isFrequencyLoading;

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
        isOpenPartnerGymSales ?
          <ContentWrapper>
            <HeaderWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text} cursor='default'>{calcDateText() + ' '}</Text>
            </HeaderWrapper>

            {/* 섹션 1: 출석률 차트 + 스크롤바 + 토글 */}
            <SectionWrapper>
                <HeaderRow>
                  <HeaderLeft>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>{viewMode === 'monthly' ? t('statistics.attendance.monthlyAttendanceRate') : t('statistics.attendance.dailyAttendanceRate')}</Text>
                    <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.subKey} cursor='default'>{calcAttendanceRate()}%</Text>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.subKey} cursor='default'>[{calcAttendedCount() + t('statistics.attendance.attendedSuffix')}]</Text>
                  </HeaderLeft>
                  <OncePerDayWrapper onClick={() => setIsCountOncePerDay(!isCountOncePerDay)}>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='pointer'>{t('statistics.attendance.oncePerDay')}</Text>
                    <CheckButton $isClicked={isCountOncePerDay}>
                      <StyledCheck $isClicked={isCountOncePerDay} />
                    </CheckButton>
                  </OncePerDayWrapper>
                </HeaderRow>

                <AttendanceChart
                  chartData={chartData}
                  viewMode={viewMode}
                  isCountOncePerDay={isCountOncePerDay}
                />

                {/* 일간/월간 토글 */}
                <ToggleWrapper>
                    <ToggleButton
                      $isActive={viewMode === 'daily'}
                      onClick={() => setViewMode('daily')}>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={viewMode === 'daily' ? theme.subBackground : theme.text} cursor='pointer'>
                        {t('statistics.attendance.daily')}
                      </Text>
                    </ToggleButton>
                    <ToggleButton
                      $isActive={viewMode === 'monthly'}
                      onClick={() => setViewMode('monthly')}>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={viewMode === 'monthly' ? theme.subBackground : theme.text} cursor='pointer'>
                        {t('statistics.attendance.monthly')}
                      </Text>
                    </ToggleButton>
                </ToggleWrapper>
            </SectionWrapper>

            {/* 섹션 2: 수업별 수강인원 */}
            <SectionWrapper>
              <ClassAttendanceList
                frequencyData={frequencyData}
                calcDateText={calcDateText}
              />
            </SectionWrapper>

            {/* 섹션 3: 출석 순위 */}
            <SectionWrapper>
              <AttendanceRankingList
                data={processedAttendanceRanking}
                selectedPartnerGym={selectedPartnerGym}
                calcDateText={calcDateText}
              />
            </SectionWrapper>
          </ContentWrapper>
          :
          <NotOpenTextWrapper>
            <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText} cursor='default'>
              {t('statistics.partnerGymPrivate')}
            </Text>
            <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText} cursor='default'>
              {t('statistics.partnerGymPrivateDesc')}
            </Text>
          </NotOpenTextWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin: 38px 0;
  padding: 20px 40px;

  width: 960px;

  border-radius: 20px;
  box-shadow: ${props => props.$isDark ? 'none' : '0 40px 50px rgba(240, 232, 227, 0.5)'};
  background-color: ${props => props.theme.subBackground};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
  height: 30px;
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;
`;

const OncePerDayWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 12px;

  &:hover {
    cursor: pointer;
  }
`;

const CheckButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 21px;
  height: 21px;

  border: ${props => props.$isClicked ? `1px solid ${props.theme.subKey}` : `1px solid ${props.theme.line}`};
  border-radius: 4px;
  background-color: ${props => props.$isClicked ? props.theme.subKey : props.theme.subBackground};

  &:hover {
    cursor: pointer;
  }

  transition: all 0.3s ease-in-out;
`;

const StyledCheck = styled(CheckSvg)`
  flex-shrink: 0;
  width: 12px;
  height: 10px;

  opacity: ${props => props.$isClicked ? 1 : 0};

  & path {
    fill: ${props => props.theme.subBackground};
  }

  transition: all 0.3s ease-in-out;
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 400px;

  background-color: ${props => props.theme.subBackground};
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin: 20px auto;

  width: 211px;
  height: 41px;

  gap: 5px;

  border-radius: 8px;
  background-color: ${props => props.theme.buttonFill};
`;

const ToggleButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 96px;
  height: 29px;

  border-radius: 6px;
  background-color: ${props => props.$isActive ? props.theme.subKey : 'transparent'};
  box-shadow: ${props => props.$isActive ? '0px 9px 7.1px rgba(0, 0, 0, 0.06)' : 'none'};

  &:hover {
    cursor: pointer;
  }

  transition: all 0.3s ease-in-out;
`;

const NotOpenTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 200px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;